/**
 * Asset Preloader - Progressive asset loading based on screen position
 * Dynamically extracts assets from <template> elements
 */

interface AssetCollection {
  images: string[];
  videos: string[];
  audio: string[];
}

export class AssetPreloader {
  private preloadedAssets: Set<string> = new Set();
  private screens: string[] = [];
  private assetsCache: Map<string, AssetCollection> = new Map();
  private rollingLookahead = false;

  /**
   * Initialize preloader with screen list
   */
  init(screens: string[], rollingLookahead = false): void {
    this.screens = screens;
    this.rollingLookahead = rollingLookahead;
    // Wave 1: Preload first screen assets immediately
    if (screens[0]) {
      this.preloadScreenAssets(0);
    }
  }

  /**
   * Called when a screen becomes active - triggers wave preloading
   */
  onScreenOpen(screenIndex: number): void {
    if (this.rollingLookahead) {
      // The Modern treatment has many large editorial illustrations. Keep network work bounded
      // to the next three screens instead of loading the rest of the funnel on screen three.
      for (let offset = 1; offset <= 3; offset++) {
        this.preloadScreenAssets(screenIndex + offset);
      }
      return;
    }

    if (screenIndex === 0) {
      // Wave 2: Preload screens 1, 2, 3
      this.preloadScreenAssets(1);
      this.preloadScreenAssets(2);
      this.preloadScreenAssets(3);
    } else if (screenIndex === 1) {
      // Wave 3: Preload screens 4, 5, 6
      this.preloadScreenAssets(4);
      this.preloadScreenAssets(5);
      this.preloadScreenAssets(6);
    } else if (screenIndex === 2) {
      // Wave 4: Preload all remaining screens
      for (let i = 7; i < this.screens.length; i++) {
        this.preloadScreenAssets(i);
      }
    }
  }

  /**
   * Preload all assets for a specific screen index
   */
  private preloadScreenAssets(index: number): void {
    const screenName = this.screens[index];
    if (!screenName) return;

    const assets = this.extractAssetsFromTemplate(screenName);

    // Preload images (non-blocking)
    assets.images.forEach((src) => this.preloadImage(src));

    // Preload videos using link preload
    assets.videos.forEach((src) => this.preloadVideo(src));

    // Preload audio
    assets.audio.forEach((src) => this.preloadAudio(src));
  }

  /**
   * Extract asset URLs from a screen's <template> element
   */
  private extractAssetsFromTemplate(screenName: string): AssetCollection {
    // Check cache first
    if (this.assetsCache.has(screenName)) {
      return this.assetsCache.get(screenName)!;
    }

    const result: AssetCollection = { images: [], videos: [], audio: [] };

    // Get the template element directly (don't clone, just read)
    const templateId = `template-screen-${screenName}`;
    const template = document.getElementById(templateId) as HTMLTemplateElement | null;

    if (!template) {
      console.log(`[AssetPreloader] No template found for screen: ${screenName}`);
      this.assetsCache.set(screenName, result);
      return result;
    }

    const content = template.content;

    // Extract images: <img src="...">
    content.querySelectorAll('img[src]').forEach((img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('data:')) {
        result.images.push(src);
      }
    });

    // Extract videos: <video><source src="...">
    content.querySelectorAll('video source[src]').forEach((source) => {
      const src = source.getAttribute('src');
      if (src) {
        result.videos.push(src);
      }
    });

    // Also check video src attribute directly
    content.querySelectorAll('video[src]').forEach((video) => {
      const src = video.getAttribute('src');
      if (src) {
        result.videos.push(src);
      }
    });

    // Extract audio: <audio src="..."> or <audio><source src="...">
    content.querySelectorAll('audio[src], audio source[src]').forEach((audio) => {
      const src = audio.getAttribute('src');
      if (src) {
        result.audio.push(src);
      }
    });

    // Cache the result
    this.assetsCache.set(screenName, result);

    console.log(
      `[AssetPreloader] Extracted from ${screenName}:`,
      `${result.images.length} images, ${result.videos.length} videos, ${result.audio.length} audio`
    );

    return result;
  }

  /**
   * Preload an image (non-blocking)
   */
  private preloadImage(src: string): void {
    if (this.preloadedAssets.has(src)) return;
    this.preloadedAssets.add(src);

    const img = new Image();
    img.src = src;
    // The browser will cache this image for later use
  }

  /**
   * Preload a video using link preload
   */
  private preloadVideo(src: string): void {
    if (this.preloadedAssets.has(src)) return;
    this.preloadedAssets.add(src);

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'video';
    link.href = src;
    document.head.appendChild(link);
  }

  /**
   * Preload audio
   */
  private preloadAudio(src: string): void {
    if (this.preloadedAssets.has(src)) return;
    this.preloadedAssets.add(src);

    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = src;
  }
}

// Singleton instance
export const assetPreloader = new AssetPreloader();
